/**
 * Management Dashboard v2 (§4) — one-shot global workforce snapshot.
 * Route: /admin/mgmt-dashboard
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Globe, Building2, TrendingUp, TrendingDown, Briefcase, Wallet, Calendar, AlertTriangle, Clock, ShieldCheck, Loader2 } from 'lucide-react';
import { apiClient } from '../../services/api';

const Card = ({ children, className = '' }) => (<div className={`bg-white border border-black/5 rounded-2xl ${className}`}>{children}</div>);
const Tile = ({ icon: Icon, label, value, sub, tone = 'green' }) => {
  const tones = {
    green: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    amber: 'text-amber-700 bg-amber-50 border-amber-200',
    red: 'text-red-700 bg-red-50 border-red-200',
    slate: 'text-slate-700 bg-slate-50 border-slate-200',
    sky: 'text-sky-700 bg-sky-50 border-sky-200',
  }[tone];
  return (
    <Card className={`p-4 border ${tones}`}>
      <Icon className="w-4 h-4 mb-1" />
      <div className="text-3xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest font-semibold">{label}</div>
      {sub && <div className="text-[11px] mt-1 opacity-70">{sub}</div>}
    </Card>
  );
};

const ManagementDashboardV2 = () => {
  const [d, setD] = useState(null);
  useEffect(() => { apiClient.get('/hr/dashboard').then((r) => setD(r.data)).catch(() => setD({})); }, []);
  if (!d) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-[#0f5c3d]" /></div>;
  const h = d.headcount || {}; const m = d.movements || {}; const p = d.payroll || {}; const a = d.attendance || {}; const al = d.alerts || {};
  return (
    <div>
      <div className="mb-6">
        <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Management view</span>
        <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Global workforce · at a glance</h1>
        <p className="mt-1 text-sm text-[#0b1a2e]/60">Snapshot as of {(d.as_of || '').slice(0, 16).replace('T', ' ')} UTC</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Tile icon={Users} label="Total employees" value={h.total || 0} tone="green" />
        <Tile icon={TrendingUp} label="Joiners · 30d" value={m.joiners_30d || 0} tone="sky" />
        <Tile icon={TrendingDown} label="Leavers · 30d" value={m.leavers_30d || 0} tone="slate" />
        <Tile icon={Briefcase} label="Open vacancies" value={m.vacancies || 0} tone="amber" />
        <Tile icon={Wallet} label={`Payroll ${p.period || '—'}`} value={(p.total || 0).toLocaleString()} tone="green" />
        <Tile icon={Calendar} label="On leave today" value={a.on_leave_today || 0} tone="amber" />
        <Tile icon={Clock} label="Pending approvals" value={al.pending_approvals || 0} tone="red" sub={<Link to="/admin/approvals-inbox" className="underline">Open inbox</Link>} />
        <Tile icon={AlertTriangle} label="Alerts · total" value={(al.probation_ending_30d || 0) + (al.contracts_expiring_60d || 0) + (al.visas_expiring_90d || 0)} tone="red" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Card className="p-4 lg:col-span-2">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#0f5c3d] mb-3 flex items-center gap-2"><Globe className="w-4 h-4" /> By country</div>
          <div className="space-y-2">
            {(h.by_country || []).map((c) => (
              <div key={c.country} className="flex items-center gap-2" data-testid={`mgmt-country-${c.country}`}>
                <div className="w-16 text-xs font-mono text-[#0b1a2e]/70">{c.country}</div>
                <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                  <div className="h-full bg-[#0f5c3d]" style={{ width: `${Math.min(100, (c.count / (h.total || 1)) * 100)}%` }} />
                </div>
                <div className="w-14 text-right text-sm font-semibold">{c.count}</div>
              </div>
            ))}
            {(h.by_country || []).length === 0 && <div className="text-xs text-[#0b1a2e]/50 py-6 text-center">No country data yet.</div>}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#0f5c3d] mb-3 flex items-center gap-2"><Building2 className="w-4 h-4" /> By department</div>
          <ul className="space-y-1.5 text-sm">
            {(h.by_department || []).slice(0, 12).map((dp) => (
              <li key={dp.department} className="flex justify-between border-b border-black/5 pb-1">
                <span className="text-[#0b1a2e]/80 truncate">{dp.department}</span>
                <span className="font-semibold">{dp.count}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-4 border-amber-200 bg-amber-50">
          <ShieldCheck className="w-4 h-4 text-amber-700 mb-1" />
          <div className="text-2xl font-bold text-amber-900">{al.probation_ending_30d || 0}</div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-amber-800">Probation ending · 30d</div>
        </Card>
        <Card className="p-4 border-red-200 bg-red-50">
          <AlertTriangle className="w-4 h-4 text-red-700 mb-1" />
          <div className="text-2xl font-bold text-red-900">{al.contracts_expiring_60d || 0}</div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-red-800">Contracts expiring · 60d</div>
        </Card>
        <Card className="p-4 border-red-200 bg-red-50">
          <AlertTriangle className="w-4 h-4 text-red-700 mb-1" />
          <div className="text-2xl font-bold text-red-900">{al.visas_expiring_90d || 0}</div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-red-800">Visas expiring · 90d</div>
        </Card>
      </div>
    </div>
  );
};

export default ManagementDashboardV2;
